import { useHistory } from 'react-router';
import { useIsFetching } from '@zg-rentals/react-query5';

import {
  Alert,
  Box,
  Button,
  Card,
  Heading,
  List,
  Paragraph,
  Popover,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import { useCreateLeaseRenewalMutation, useLeaseQuery } from '../../../utils/queries';
import LeaseExtension from '../../../models/LeaseExtension';
import { Property } from '../../../models';
import { useLeasingUrlParams } from '../../../hooks/useLeasingUrlParams';

export default function UpdateYourCurrentTermsWithALeaseRenewalAgreement() {
  const createLeaseRenewal = useCreateLeaseRenewalMutation();
  const { leaseId, propertyId } = useLeasingUrlParams();
  const history = useHistory();
  const leaseQuery = useLeaseQuery(leaseId);
  const isFetching = useIsFetching() > 0;

  const latestExtension = leaseQuery?.data?.latestExtension ?? null;
  const lease = leaseQuery?.data;

  const endDate = lease?.getLeasePackageFriendlyEndDate();

  async function onCreateLeaseRenewalClick() {
    const response = await createLeaseRenewal.mutateAsync({
      postData: {
        leaseId: leaseId as unknown as number,
      },
    });
    history.push(LeaseExtension.getWizardPath(leaseId, String(response.leaseExtensionId)));
  }

  function onEditYourCurrentLeaseRenewalClick() {
    history.push(LeaseExtension.getWizardPath(leaseId, String(latestExtension?.id ?? '')));
  }

  function onCreateANewLeaseClick() {
    history.push(Property.getCombinedOnboardingPath(propertyId));
  }

  const isLatestLeaseRenewalNotRatified = latestExtension ? !latestExtension?.isReadOnly() : false;

  return (
    <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'default' }}>
      <Box css={{ gridColumnEnd: { base: 'span 12', xl: 'span 8' } }}>
        <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'loose' }}>
          Update your current terms with a lease renewal agreement
        </Heading>
        <Paragraph css={{ marginBottom: 'loose' }}>
          This agreement is a short amendment to your current lease, sparing you the trouble of creating a whole new
          lease.
          {endDate && latestExtension && (
            <>
              {' '}
              Your lease term ends on <Text css={{ fontWeight: 'bold' }}>{endDate}</Text>, and the lease renewal will
              start on the following day.
            </>
          )}
        </Paragraph>
        <Card tone="neutral" elevated={false} css={{ marginBottom: 'loose' }}>
          {isLatestLeaseRenewalNotRatified && !isFetching && (
            <Alert
              tone="warning"
              actionButton={
                <TextButton onClick={onEditYourCurrentLeaseRenewalClick}>Edit your current lease renewal</TextButton>
              }
              css={{ marginBottom: 'default' }}
            >
              You can only have one lease renewal draft at a time. Sign your current draft to finalize it, or delete it
              in order to create a new lease renewal agreement.
            </Alert>
          )}
          {!lease?.isEligibleForLeaseRenewal() && !isLatestLeaseRenewalNotRatified && !isFetching && (
            <Alert
              tone="warning"
              actionButton={<TextButton onClick={onCreateANewLeaseClick}>Create new lease</TextButton>}
              css={{ marginBottom: 'default' }}
            >
              <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
                <Text>This lease can’t be renewed</Text>
                <Popover
                  header={<Heading level={3}>Lease renewal eligibility</Heading>}
                  body={
                    <>
                      <Paragraph>Your lease must meet these requirements for renewal:</Paragraph>
                      <List.Root appearance="bullet">
                        <List.Item>It’s a Zillow lease</List.Item>
                        <List.Item>It’s currently active or expired</List.Item>
                        <List.Item>It has a fixed end date</List.Item>
                        <List.Item>It doesn’t already have an existing lease renewal agreement</List.Item>
                      </List.Root>
                    </>
                  }
                />
              </Box>
            </Alert>
          )}
          <Heading level={3} textStyle="heading-xs" css={{ marginBottom: 'default' }}>
            Create your own
          </Heading>
          <Paragraph css={{ marginBottom: 'default' }}>
            Update your terms as needed, then send your renewal for signing — all in only a few minutes
          </Paragraph>
          <Button
            css={{ width: { base: '100%', xl: 'auto' } }}
            disabled={isLatestLeaseRenewalNotRatified || !lease?.isEligibleForLeaseRenewal()}
            tone="brand"
            emphasis="filled"
            onClick={onCreateLeaseRenewalClick}
          >
            Create lease renewal
          </Button>
        </Card>
      </Box>
    </Box>
  );
}
